import * as React from 'react'

import { Box, Icon, Table, colors } from '@factorialco/deprecated-design-system'

import TextField from 'modules/core/components/TextField'
import { Controls } from 'modules/core/hooks/usePaginatedQuery'
import i18n from 'modules/core/lib/i18n'

import { CostCenterOption } from 'modules/cost_centers/components/CostCenterAssigments/components/AssignmentsManager/Picker/hooks/useCostCenterByLegalEntities'
import { DEFAULT_ASSIGNMENT_PERCENTAGE } from 'modules/cost_centers/components/CostCenterAssigments/lib/defaults'
import { FormattedAssignment } from 'modules/cost_centers/components/CostCenterAssigments/lib/types'

import JobStructurePicker from './JobStructurePicker'
import WorkplacePicker from './WorkplacePicker'
import * as styles from './index.vanilla.css'

export interface StructuredValueUpdate {
  jobCatalogCostCenterId?: number
  jobCatalogNodeUuid?: string
  workplaceCostCenterId?: number
  workplaceId?: number
  percentage: number
  name?: string
}

interface Props {
  showWorkplaceColumn: boolean
  showJobStructureColumn: boolean
  assignment: FormattedAssignment
  jobStructureOptions: CostCenterOption[]
  workplaceOptions: CostCenterOption[]
  selectedJobCatalogCostCenterIds: number[]
  selectedWorkplaceCostCenterIds: number[]
  controls?: Controls
  loading?: boolean
  readonly?: boolean
  onDelete: (assignmentId: string) => void
  onJobStructureChange: (assignmentId: string, option: CostCenterOption | null) => void
  onWorkplaceChange: (assignmentId: string, option: CostCenterOption | null) => void
  onPercentageChange: (assignmentId: string, percentage: number) => void
  onSearchChange: (search: string) => void
}

const StructuredValueRow = ({
  showWorkplaceColumn,
  showJobStructureColumn,
  assignment,
  jobStructureOptions,
  workplaceOptions,
  selectedJobCatalogCostCenterIds,
  selectedWorkplaceCostCenterIds,
  controls,
  loading,
  readonly,
  onDelete,
  onJobStructureChange,
  onWorkplaceChange,
  onPercentageChange,
  onSearchChange,
}: Props) => {
  const [hover, setHover] = React.useState<boolean>(false)
  const hasNotPercentage = assignment.percentage === DEFAULT_ASSIGNMENT_PERCENTAGE
  const notValid = !hasNotPercentage && (assignment.percentage <= 0 || assignment.percentage > 1)

  // When assignment is default, disable the cost center selectors (only % editable)
  const selectorsReadonly = readonly || assignment.isDefault

  // Build the current job structure value for display by looking up from options
  const jobStructureValue = assignment.jobCatalogCostCenterId
    ? {
        id: String(assignment.jobCatalogCostCenterId),
        name:
          jobStructureOptions.find((o) => o.id === String(assignment.jobCatalogCostCenterId))
            ?.name || '',
      }
    : undefined

  // Build the current workplace value for display by looking up from options
  const workplaceValue = assignment.workplaceCostCenterId
    ? {
        id: String(assignment.workplaceCostCenterId),
        name:
          workplaceOptions.find((o) => o.id === String(assignment.workplaceCostCenterId))?.name ||
          '',
      }
    : undefined

  const handleJobStructureChange = (option: CostCenterOption) => {
    onJobStructureChange(assignment.id, option)
  }

  const handleWorkplaceChange = (option: CostCenterOption) => {
    onWorkplaceChange(assignment.id, option)
  }

  const handlePercentageChange = (newValue: string) => {
    const percentage = newValue === '' ? DEFAULT_ASSIGNMENT_PERCENTAGE : Number(newValue) / 100
    onPercentageChange(assignment.id, percentage)
  }

  return (
    <Table.Row>
      {/* Job Structure Column */}
      {showJobStructureColumn && (
        <Table.Cell
          padding="s0"
          wide
          style={{
            ...(jobStructureOptions.length === 0 && {
              backgroundColor: colors.grey200,
              cursor: 'pointer',
            }),
            background: selectorsReadonly ? colors.grey200 : 'transparent',
          }}
        >
          <Box className={styles.borderlessSelector}>
            <JobStructurePicker
              value={jobStructureValue}
              loading={loading}
              options={jobStructureOptions}
              selectedIds={selectedJobCatalogCostCenterIds}
              controls={controls}
              onChange={handleJobStructureChange}
              onSearchChange={onSearchChange}
              readonly={selectorsReadonly}
              placeholder={i18n.t(
                'finance.cost_centers.assignments.structured.select_job_structure'
              )}
            />
          </Box>
        </Table.Cell>
      )}
      {/* Workplace Column */}
      {showWorkplaceColumn && (
        <Table.Cell
          padding="s0"
          wide
          style={{
            ...(workplaceOptions.length === 0 && {
              backgroundColor: colors.grey200,
              cursor: 'pointer',
            }),
            background: selectorsReadonly ? colors.grey200 : 'transparent',
          }}
        >
          <Box
            className={styles.borderlessSelector}
            border={{
              left: {
                width: 's1',
                style: 'solid',
                color: 'darker160',
              },
            }}
          >
            <WorkplacePicker
              value={workplaceValue}
              loading={loading}
              options={workplaceOptions}
              selectedIds={selectedWorkplaceCostCenterIds}
              controls={controls}
              onChange={handleWorkplaceChange}
              onSearchChange={onSearchChange}
              readonly={selectorsReadonly}
              placeholder={i18n.t('finance.cost_centers.assignments.structured.select_workplace')}
            />
          </Box>
        </Table.Cell>
      )}

      {/* Percentage Column */}
      <Table.Cell padding="s0" width={120}>
        <Box
          className={
            `${styles.borderlessInput}` + `${notValid ? ` ${styles.errorBorderlessInput}` : ''}`
          }
          justifyContent="center"
          height="full"
          border={{
            left: {
              width: 's1',
              style: 'solid',
              color: 'darker160',
            },
          }}
        >
          <TextField
            {...(!hasNotPercentage && { defaultValue: (assignment.percentage * 100).toFixed(2) })}
            onChange={handlePercentageChange}
            type="number"
            min={0.0}
            readonly={readonly}
            step={'5'}
            placeholder={i18n.t(
              'finance.employee_dimensions_info.modals.set_assignments.default_value'
            )}
            isNotValid={notValid}
            onKeyDown={(e) => {
              if (e.key === '-') {
                e.preventDefault()
              }
            }}
            hideError
          />
        </Box>
      </Table.Cell>

      {/* Delete Button Column */}
      <Table.Cell width={40} padding="s0">
        <Box
          paddingX="s8"
          paddingY="s12"
          background={hover && !selectorsReadonly ? 'grey300' : 'grey100'}
          alignItems="center"
          cursor={selectorsReadonly ? 'default' : 'pointer'}
          border={{
            left: {
              width: 's1',
              color: 'grey400',
              style: 'solid',
            },
          }}
          onMouseEnter={() => !selectorsReadonly && setHover(true)}
          onMouseLeave={() => !selectorsReadonly && setHover(false)}
          onClick={() => !selectorsReadonly && onDelete(assignment.id)}
        >
          <Icon.Medium icon="delete" color={selectorsReadonly ? 'grey400' : undefined} />
        </Box>
      </Table.Cell>
    </Table.Row>
  )
}

export default StructuredValueRow
